import React, { useRef, useEffect, useState } from "react";
import Chart from "chart.js";
import lineChartConfiguration from "./lineChartConfiguration";

import "./LineChart.css";

export type Props = {
    data: number[];
    labels: string[];
    title?: string;
    yLabel?: string;
    xLabel?: string;
    valueUnit?: string;
    download?: boolean;
    lineStyle?: 'curved' | 'straight'
    preventRenderingQuarters?: boolean; // Setting this to 'true' will only render the year once for labels on the format 'YYYY-Qn'
};

const LineChart: React.FC<Props> = props => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart>();
    const [image, setImage] = useState<string>("");

    const preventRenderingQuartersConfig = props.preventRenderingQuarters ? {
        maxRotation: 0,
        callback: (value: any, index: any, values: any) => {
            if (/Q1/.test(value)) {
                return value.substring(0,4)
            }
            return null;
        },
    } : {}

    const getChartInstance = (): Chart | null => {
        if (chartRef.current) {
            return chartRef.current;
        } else {
            if (!canvasRef.current) {
                return null;
            }

            chartRef.current = new Chart(canvasRef.current, lineChartConfiguration(props, setImage));

            Chart.plugins.register({
                beforeDraw: function(chartInstance) {
                    const ctx = chartInstance.ctx;

                    if (ctx == null) {
                        return;
                    }

                    ctx.fillStyle = "white";
                    ctx.fillRect(0, 0, chartInstance.width ?? 0, chartInstance.height ?? 0);
                }
            });

            return chartRef.current;
        }
    };

    useEffect(() => {
        const chart = getChartInstance();

        if (chart != null && chart.data.datasets && chart.options.scales?.yAxes && chart.options.scales?.xAxes) {
            chart.data.datasets[0].data = props.data;
            
            if (props.lineStyle === 'straight') {
                chart.data.datasets[0].lineTension = 0
            }

            chart.data.labels = props.labels;
            chart.options.scales.yAxes[0] = {
                ticks: {
                    ...chart.options.scales.yAxes[0].ticks
                },
                scaleLabel: {
                    ...chart.options.scales.yAxes[0].scaleLabel,
                    display: props.xLabel !== undefined,
                    labelString: props.yLabel
                }
            };

            chart.options.scales.xAxes[0] = {
                ...chart.options.scales.xAxes[0],
                ticks: {
                    ...chart.options.scales.xAxes[0].ticks,
                    ...preventRenderingQuartersConfig,                    
                },
                scaleLabel: {
                    ...chart.options.scales.xAxes[0].scaleLabel,
                    display: props.xLabel !== undefined,
                    labelString: props.xLabel
                },
            };
            chart.update();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, props.labels, props.title, props.yLabel, props.xLabel]);

    return (
        <div className="LineChart">
            <div className="LineChart-header">
                <span className="LineChart-title">{props.title}</span>
            </div>

            {props.data.length === 0 ? (
                <div className="LineChart-centerOverlay">No data found for current filters.</div>
            ) : null}
            <canvas ref={canvasRef} style={{ visibility: props.data.length === 0 ? "hidden" : "visible" }} />

            {props.download ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                    <a href={image} className="Button" download={props.title?.replace(/\s/g, "-") ?? "chart.png"}>
                        Save as image
                    </a>
                </div>
            ) : null}
        </div>
    );
};

export default LineChart;
